import { HttpClient, HttpContext, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { Observable, tap } from "rxjs";
import { upload, Upload } from "../../partials/upload/upload";
import {
  Country,
  Currency,
  EvidenceAddition,
  EvidenceInfo,
  Incident,
  Incidents,
  Invoice,
  Login,
  Offer,
  Package,
  ParamsCreateTransaction,
  ScamInfo,
  Transaction,
  UserEntity,
  VictimInfo,
  AppSetting,
  IncidentReport,
  PreinvestigationReport,
  InvoiceSummary,
} from "../models/safe-crypto.models";
import { SKIP_SESSION_INTERCEPTOR } from "../../http-context.tokens";

/** Where the rotating refresh credential lives. Session id stays under `token`. */
export const REFRESH_TOKEN_KEY = 'smc.refreshToken';
import { LanguageEnum } from "../../partials/utils/language";

@Injectable({
  providedIn: 'root'
})
export class SafeCryptoService {
  public actualIncidentId!: string;
  private _apiUrl: string = environment.apiUrl;
  private _httpClient: HttpClient = inject(HttpClient);
  context = new HttpContext().set(SKIP_SESSION_INTERCEPTOR, true);

  // `initData` is the raw Telegram WebApp string, verified server-side by HMAC
  // against the bot token. Optional: the browser-testing path has no Telegram
  // context, and the BE runs warn-only until real traffic carries it.
  login(body: { telegramChatId: string, telegramUserId: string, telegramUsername: string, fullName: string, language?: LanguageEnum, initData?: string }): Observable<Login> {
    return this._httpClient.post<Login>(`${ this._apiUrl }/api/v1/users/login`, body).pipe(
      tap((login) => this._storeSession(login))
    );
  }

  /**
   * Renews an expired session from the refresh token instead of re-asserting
   * identity. The presented token is consumed server-side and a replacement
   * comes back on the response, so the store must be updated from the reply —
   * never reuse the token that was sent.
   *
   * Skips the session interceptor: this IS the session-recovery call, so a 401
   * here means the credential is spent and there is nothing left to retry.
   */
  refreshSession(refreshToken: string): Observable<Login> {
    return this._httpClient.post<Login>(
      `${ this._apiUrl }/api/v1/users/refresh`,
      { refreshToken },
      { context: new HttpContext().set(SKIP_SESSION_INTERCEPTOR, true) },
    ).pipe(
      tap((login) => this._storeSession(login))
    );
  }

  private _storeSession(login: Login): void {
    if (login?.sessionId) {
      localStorage.setItem('token', login.sessionId);
    }
    // Absent on an older BE build; keep whatever we already hold rather than
    // clearing a credential that is still good.
    if (login?.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, login.refreshToken);
    }
    if (login?.actualIncidentId) {
      this.actualIncidentId = login.actualIncidentId;
    }
  }

  putUserAcceptTerms(userId: string): Observable<null> {
    return this._httpClient.put<null>(`${ this._apiUrl }/api/v1/users/${ userId }/accept-terms`, {});
  }

  // Persists the user's explicit language choice (dropdown). The BE also
  // overwrites language on every login, so the login payload must repeat the
  // same value for the choice to survive a re-entry — see resolveLoginLanguage.
  putUserLanguage(userId: string, language: LanguageEnum): Observable<null> {
    return this._httpClient.put<null>(`${ this._apiUrl }/api/v1/users/${ userId }/language`, { language });
  }

  putLeadStatus(leadStatus: 'PREVIEW' | 'SUBMITTED' | 'SCAM' | 'VICTIM' | 'TRANSACTION' | 'EVIDENCE' | 'CANCELED'): Observable<null> {
    this._checkActualIncidentId();
    return this._httpClient.put<null>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/lead-status`, { leadStatus: leadStatus });
  }

  getUser(userId: string): Observable<UserEntity> {
    return this._httpClient.get<UserEntity>(`${ this._apiUrl }/api/v1/users/${ userId }`).pipe(tap(({ actualIncidentId }) => {
      if (actualIncidentId) {
        this.actualIncidentId = actualIncidentId;
      }
    }));
  }

  getGeo(userId: string): Observable<{actualGeo: string}> {
    return this._httpClient.get<{actualGeo: string}>(`${ this._apiUrl }/api/v1/users/${userId}/geo`);
  }

  putGeo(userId: string, country: string): Observable<any> {
    return this._httpClient.put<any>(`${ this._apiUrl }/api/v1/users/${userId}/geo`, {country});
  }

  // Multi-invoice flow. BE splits the submitted options: all FIXED_AMOUNT
  // options collapse into one PAYMENT_WAITING invoice; each fee-only option
  // gets its own POSTPONED invoice. Idempotent on the option-id set.
  // optionIds are per-incident bundle.recoveryOptions[].id values (not
  // catalog package ids).
  startRecovery(incidentId: string, offerId: string, optionIds: string[]): Observable<InvoiceSummary[]> {
    return this._httpClient.post<InvoiceSummary[]>(
      `${ this._apiUrl }/api/v1/incidents/${incidentId}/offers/${offerId}/start-recovery`,
      { optionIds },
    );
  }

  postCreateIncident(userId: string): Observable<{ id: string }> {
    const body = { userId }
    return this._httpClient.post<{ id: string }>(`${ this._apiUrl }/api/v1/incidents`, body).pipe(tap(({ id }) => {
      this.actualIncidentId = id
    }));
  }

  cancelIncident(incidentId: string): Observable<null> {
    return this._httpClient.put<null>(`${ this._apiUrl }/api/v1/incidents/${ incidentId }/lead-status`, { leadStatus: 'CANCELED' });
  }

  getIncident(): Observable<Incident> {
    this._checkActualIncidentId();
    return this._httpClient.get<Incident>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }`);
  }

  getScamInfoIncident(): Observable<ScamInfo> {
    this._checkActualIncidentId();
    return this._httpClient.get<ScamInfo>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/scam`);
  }

  // Upsert scam info for the current incident. BE uses POST as an idempotent
  // upsert with IGNORE-nulls semantics; PATCH is not supported on this route.
  saveScamInfo(body: ScamInfo): Observable<null> {
    this._checkActualIncidentId();
    return this._httpClient.post<null>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/scam`, body);
  }

  // Upsert victim info (POST = idempotent upsert; PATCH not supported).
  saveVictimInfo(body: VictimInfo): Observable<null> {
    this._checkActualIncidentId();
    return this._httpClient.post<null>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/victim`, body);
  }

  getTransactions(): Observable<Transaction[]> {
    this._checkActualIncidentId();
    return this._httpClient.get<Transaction[]>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/transactions`);
  }

  postCreateTransactions(body: ParamsCreateTransaction): Observable<Transaction[]> {
    this._checkActualIncidentId();
    return this._httpClient.post<Transaction[]>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/transactions`, body);
  }

  deleteCreateTransactions(idTransaction: string): Observable<null> {
    this._checkActualIncidentId();
    return this._httpClient.delete<null>(`${ this._apiUrl }/api/v1/incidents/${this.actualIncidentId}/transactions/${idTransaction}`);
  }

  getCurrency(): Observable<Currency[]> {
    return this._httpClient.get<Currency[]>(`${ this._apiUrl }/api/v1/networks`);
  }

  networkValid(network: string, body: {hash?: string, wallet?: string}): Observable<{isValid: boolean}> {
    let params = new HttpParams();
    body.hash && (params = params.append('hash', body.hash));
    body.wallet && (params = params.append('wallet', body.wallet));
    return this._httpClient.get<{isValid: boolean}>(`${ this._apiUrl }/api/v1/networks/${network}/valid`, {params});
  }

  getInfoTransaction(hash: string, network: string): Observable<any> {
    const params = new HttpParams({
      fromObject: {
        hash, network
      }
    });
    return this._httpClient.get<any>(`${ this._apiUrl }/api/v1/transactions`, {params});
  }

  // evidence-info-controller
  getEvidence(): Observable<EvidenceInfo> {
    this._checkActualIncidentId();
    return this._httpClient.get<EvidenceInfo>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/evidence`);
  }

  putEvidence(body: Partial<EvidenceInfo>): Observable<never> {
    this._checkActualIncidentId();
    return this._httpClient.put<never>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/evidence`, body);
  }

  postEvidence(body: Omit<Partial<EvidenceInfo>, 'id'>): Observable<{ id: string }> {
    this._checkActualIncidentId();
    return this._httpClient.post<{
      id: string
    }>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/evidence`, body);
  }

  patchEvidence(body: Partial<EvidenceInfo>): Observable<never> {
    this._checkActualIncidentId();
    return this._httpClient.patch<never>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/evidence`, body);
  }

  getEvidenceById(id: string): Observable<EvidenceInfo> {
    this._checkActualIncidentId();
    return this._httpClient.get<EvidenceInfo>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }`);
  }

  putEvidenceById(id: string, body: Partial<never>): Observable<EvidenceInfo> {
    this._checkActualIncidentId();
    return this._httpClient.put<never>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }`, body);
  }

  patchEvidenceById(id: string, body: Partial<never>): Observable<EvidenceInfo> {
    this._checkActualIncidentId();
    return this._httpClient.patch<never>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }`, body);
  }

  getIncidents(userId: string): Observable<Incidents> {
    const params = new HttpParams({
      fromObject: {
        userId
      }
    });
    return this._httpClient.get<Incidents>(`${ this._apiUrl }/api/v1/incidents`, { params });
  }

  getIncidentWithId(incidentId: string): Observable<Incident> {
    return this._httpClient.get<Incident>(`${ this._apiUrl }/api/v1/incidents/${incidentId}`);
  }

  getEvidenceWithId(incidentId: string): Observable<EvidenceInfo | null> {
    return this._httpClient.get<EvidenceInfo | null>(`${ this._apiUrl }/api/v1/incidents/${incidentId}/evidence`);
  }

  getOffer(incidentId: string, offerId: string): Observable<Offer> {
    return this._httpClient.get<Offer>(`${ this._apiUrl }/api/v1/incidents/${incidentId}/offers/${offerId}`);
  }

  getPackageInvoices({incidentId, offerId, packageId}: {incidentId: string, offerId: string, packageId: string}): Observable<Invoice[]> {
    return this._httpClient.get<Invoice[]>(`${ this._apiUrl }/api/v1/incidents/${incidentId}/offers/${offerId}/packages/${packageId}/invoices`);
  }

  getInvoice({incidentId, offerId, invoiceId}: {incidentId: string, offerId: string, invoiceId: string}): Observable<Invoice> {
    return this._httpClient.get<Invoice>(`${ this._apiUrl }/api/v1/incidents/${incidentId}/offers/${offerId}/invoices/${invoiceId}`);
  }

  getPackage(packageId: string): Observable<Package> {
    return this._httpClient.get<Package>(`${ this._apiUrl }/api/v1/offer-packages/${packageId}`);
  }

  postRequestOffer(incidentId: string): Observable<any> {
    return this._httpClient.post<any>(`${ this._apiUrl }/api/v1/incidents/${incidentId}/request-offer`, {});
  }

  // Deliberately its own endpoint rather than a field on the case bundle: the
  // bundle is cached with Last-Modified / If-Modified-Since keyed on case
  // activity, so a settings change wouldn't invalidate it and we'd serve a
  // stale chat link. Fetch it independently.
  getSettings(): Observable<AppSetting[]> {
    return this._httpClient.get<AppSetting[]>(`${ this._apiUrl }/api/v1/settings`);
  }

  getReports(incidentId: string): Observable<IncidentReport[]> {
    return this._httpClient.get<IncidentReport[]>(`${ this._apiUrl }/api/v1/incidents/${incidentId}/reports`);
  }

  getPreinvestigationReport(incidentId: string): Observable<PreinvestigationReport> {
    return this._httpClient.get<PreinvestigationReport>(`${ this._apiUrl }/api/v1/users/incidents/${incidentId}/preinvestigation-report`);
  }

  getOfferPhoto(incidentId: string, offerId: string): Observable<string[]> {
    return this._httpClient.get<string[]>(`${ this._apiUrl }/api/v1/incidents/${incidentId}/offers/${offerId}/photos`);
  }

  getFile(id: string): Observable<Blob> {
    return this._httpClient.get(`${this._apiUrl}/api/v1/files/${id}`, {
      responseType: "blob",
    });
  }

  // Inline endpoint is @PreAuthorize-gated, so plain <img src> can't reach it
  // (no x-auth-session header). Fetch via HttpClient and bind a blob: URL.
  getInlineFile(fileId: string): Observable<Blob> {
    return this._httpClient.get(`${this._apiUrl}/api/v1/files/inline/${fileId}`, {
      responseType: "blob",
    });
  }

  // ------------------------

  // evidence-addition-controller
  getEvidenceAdditionById(evidenceId: string, id: string): Observable<EvidenceAddition> {
    this._checkActualIncidentId();
    return this._httpClient.get<EvidenceAddition>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/evidence/additions/${ id }`);
  }

  putEvidenceAdditionById(evidenceId: string, id: string, body: Partial<EvidenceAddition>): Observable<EvidenceAddition> {
    this._checkActualIncidentId();
    return this._httpClient.put<EvidenceAddition>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/evidence/additions/${ id }`, body);
  }

  deleteEvidenceAdditionById(evidenceId: string, id: string): Observable<never> {
    this._checkActualIncidentId();
    return this._httpClient.delete<never>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/evidence/additions/${ id }`);
  }

  postEvidenceAddition(evidenceId: string, body: Partial<EvidenceAddition>): Observable<EvidenceAddition> {
    this._checkActualIncidentId();
    return this._httpClient.post<EvidenceAddition>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/evidence/additions`, body);
  }

  postEvidenceAdditionUploadFile(evidenceId: string, file: File): Observable<Upload<{ id: string }>> {
    this._checkActualIncidentId();
    const body = new FormData();
    body.append('file', file);

    const params = new HttpParams({
      fromObject: {
        name: file.name,
        fileType: file.type
      }
    });

    return this._httpClient.post<{
      id: string
    }>(`${ this._apiUrl }/api/v1/incidents/${ this.actualIncidentId }/evidence/additions/uploadFile`,
      body,
      {
        params,
        reportProgress: true,
        observe: 'events',
      }).pipe(upload<{ id: string }>());
  }

  // ------------------------

  private _checkActualIncidentId(): void {
    if (!this.actualIncidentId) {
      throw new Error("In Service not find incident Id");
    }
  }


  getCountries() {
    return this._httpClient.get<Country[]>(`${ this._apiUrl }/api/v1/countries`);
  }
}
